import { useState, useRef } from "react";
import SolidarityIcon from "../Assets/Icons/SolidarityIcon";
import { toast, ToastContainer } from "react-toastify";
import {
  BookOpenIcon,
  UserCircleIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";

const ApiDocsLogin = () => {
  // UI state
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Refs to preserve focus/caret when toggling visibility
  const passwordRef = useRef(null);

  const togglePasswordVisibility = () => {
    const el = passwordRef?.current;
    const selStart = el?.selectionStart ?? null;
    const selEnd = el?.selectionEnd ?? null;
    setShowPass((s) => !s);
    requestAnimationFrame(() => {
      if (el) {
        el.focus({ preventScroll: true });
        if (selStart !== null && selEnd !== null) {
          try {
            el.setSelectionRange(selStart, selEnd);
          } catch {}
        }
      }
    });
  };

  const LoginAPI = async () => {
    const url = (process.env.REACT_APP_PAYMENT_API || "") + "Readme/LoginForReadme";
    const data = { UserName: email, Password: password };

    try {
      setLoading(true);
      setError("");
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorDetails = await response.json().catch(() => ({}));
        
        toast.error(errorDetails.message || "Failed to login", {
          style: { background: "#ffefef", color: "#7f1d1d", fontWeight: 600 },
        });
        setLoading(false);
        return;
      }

      const result = await response.json();

      if (result.message === "Login successful" && result.redirectUrl) {
        
        toast.success("Login successful! Redirecting...", {
          style: { background: "#f0fdf4", color: "#14532d", fontWeight: 600 },
        });
        setLoading(false);
        // Redirect to the URL provided by the API
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 1000);
      } else {
        setLoading(false);
        toast.error(result.message || "Login failed", {
          style: { background: "#ffefef", color: "#7f1d1d", fontWeight: 600 },
        });
      }
    } catch (err) {
      setLoading(false);
      console.error("Login error:", err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Both email and password are required!");
      return;
    }
    await LoginAPI();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-sky-50">
      <div className="mx-auto flex min-h-screen max-w-7xl items-center px-4 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 lg:grid-cols-2">
          {/* Left brand section */}
          <div className="hidden lg:flex flex-col justify-center">
            <div className="flex items-center gap-3">
              <SolidarityIcon className="h-10 w-auto" size="h-10" />
              <div className="text-2xl font-semibold text-gray-900">
                Solidarity Hub
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm text-gray-600">
              Access Solidarity API documentation and resources. Please sign in
              using your credentials to view technical documentation and API
              references.
            </p>

            <div className="mt-10 hidden xl:block">
              <div className="rounded-2xl border border-gray-200 bg-white/70 p-5 shadow-sm backdrop-blur">
                <div className="text-sm font-medium text-gray-900">
                  API Documentation
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Browse comprehensive guides, API references, and integration
                  tutorials for Solidarity systems.
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                  <BookOpenIcon className="h-4 w-4" />
                  <span>Secure access to technical resources</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right login card */}
          <div className="flex w-full justify-center">
            <div className="w-full max-w-md">
              <div className="mb-6 flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-sm">
                  <BookOpenIcon className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    API Documentation
                  </h1>
                  <p className="text-sm text-gray-500">
                    Sign in to access documentation
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm backdrop-blur">
                <form className="space-y-5" onSubmit={handleLogin}>
                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Email
                    </label>
                    <div className="mt-1 relative">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@solidarity.com.bh"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-lg border-0 py-2 pr-3 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-sky-600 sm:text-sm"
                      />
                      <UserCircleIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Password
                    </label>
                    <div className="mt-1 relative">
                      <input
                        ref={passwordRef}
                        id="password"
                        name="password"
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        className="block w-full rounded-lg border-0 py-2 pr-10 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-sky-600 sm:text-sm"
                      />
                      <LockClosedIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={togglePasswordVisibility}
                        className="absolute right-2 top-1.5 rounded-md p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-600"
                        aria-label={showPass ? "Hide password" : "Show password"}
                      >
                        {showPass ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="relative flex w-full items-center justify-center overflow-hidden rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Signing in…
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign in
                      </span>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ApiDocsLogin;
